import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Check } from 'lucide-react';
import { motion } from 'framer-motion';

export const ProblemSolution = () => {
  const { t } = useLanguage();

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  const problems = [
    t('problem_1'),
    t('problem_2'),
    t('problem_3'),
    t('problem_4'),
  ];

  const solutions = [
    t('solution_1'),
    t('solution_2'),
    t('solution_3'),
    t('solution_4'),
  ];

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="problem-solution-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Problem Card */}
          <motion.div 
            {...fadeUp}
            className="bg-slate-50 rounded-2xl p-8 md:p-10 border border-slate-100"
            data-testid="problem-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-full text-red-600 text-sm font-medium mb-6">
              <X className="w-4 h-4" />
              {t('problem_title')}
            </div>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" />
                  </div>
                  <span className="text-slate-600 text-base">{problem}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solution Card */}
          <motion.div 
            {...fadeUp}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-slate-900 rounded-2xl p-8 md:p-10 text-white"
            data-testid="solution-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/20 rounded-full text-green-400 text-sm font-medium mb-6">
              <Check className="w-4 h-4" />
              {t('solution_title')}
            </div>
            <div className="space-y-4">
              {solutions.map((solution, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  </div>
                  <span className="text-slate-200 text-base">{solution}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
